"use client";

import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Burger, Group, Stack, Text, Title, Container, ActionIcon, Tooltip, Card, useMantineColorScheme, Box, Paper, Overlay, Transition, Portal } from "@mantine/core";
import "@bi-studio/shared/styles/theme.css";
import { useAuth } from "../../hooks/use-auth";
import { LogOut, Building2, Settings, LayoutDashboard } from "lucide-react";
import { ThemeSwitch } from "./theme-switch";

interface AppLayoutProps {
  children: React.ReactNode;
  showSidebar?: boolean;
  sidebarContent?: React.ReactNode;
  title?: string;
  subtitle?: string;
  onLogout?: () => void;
  headerActions?: React.ReactNode;
  hideHeader?: boolean;
  hideDashboardButton?: boolean;
  hideSettingsButton?: boolean;
  floatingContent?: React.ReactNode;
}

export function AppLayout({
  children,
  showSidebar = false,
  sidebarContent,
  title = "Workspace",
  subtitle = "BI Studio",
  onLogout,
  headerActions,
  hideHeader = false,
  hideDashboardButton = false,
  hideSettingsButton = false,
  floatingContent,
}: AppLayoutProps) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);

  const handleLogout = () => {
    logout();
    onLogout?.();
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Sync Mantine color scheme with Tailwind .dark class
  React.useEffect(() => {
    if (colorScheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [colorScheme]);

  // Close mobile menu on route change
  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  return (
    <Box
      className="min-h-screen flex flex-col transition-colors duration-200"
      style={{
        fontFamily: 'var(--mantine-font-family)',
        backgroundColor: 'var(--mantine-color-body)',
        color: 'var(--mantine-color-text)',
        backgroundImage: 'var(--bi-studio-background-gradient)',
      }}
    >
      {!hideHeader && (
        <header
          className="border-b sticky top-0 z-50 transition-colors duration-200"
          style={{
            backgroundColor: 'var(--bi-studio-header-bg)',
            color: 'var(--mantine-color-text)',
          }}
        >
          <Container size="xl" px="md">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-3 min-w-0">
                {showSidebar && (
                  <Group gap="xs">
                    <Burger opened={mobileMenuOpen} onClick={toggleMobileMenu} hiddenFrom="sm" size="sm" />
                    <Tooltip label={isSidebarCollapsed ? "Show Sidebar" : "Hide Sidebar"}>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                        visibleFrom="sm"
                        size="lg"
                      >
                        <svg
                          className={`h-5 w-5 transition-transform duration-300 ${isSidebarCollapsed ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                          />
                        </svg>
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                )}
                <div className="min-w-0">
                  <Text size="xs" fw={500} c="dimmed" truncate>
                    {subtitle}
                  </Text>
                  <Title order={3} size="h4" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {title}
                  </Title>
                </div>
              </div>

              <Group gap="xs">
                {headerActions && (
                  <Group gap="xs" visibleFrom="sm">
                    {headerActions}
                  </Group>
                )}

                <ThemeSwitch />

                <Button
                  variant="subtle"
                  color="gray"
                  onClick={handleLogout}
                  size="sm"
                  leftSection={<LogOut size={16} />}
                  visibleFrom="sm"
                >
                  Logout
                </Button>
              </Group>
            </div>
          </Container>
        </header>
      )}

      {/* Mobile Menu Overlay */}
      <Portal>
        <Transition mounted={mobileMenuOpen} transition="fade" duration={200}>
          {(styles) => (
            <Overlay
              fixed
              zIndex={40}
              style={{ ...styles, backgroundColor: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(4px)', top: '64px' }}
              onClick={() => setMobileMenuOpen(false)}
              className="sm:hidden"
            />
          )}
        </Transition>

        <Transition mounted={mobileMenuOpen} transition="slide-down" duration={250} timingFunction="ease">
          {(styles) => (
            <Paper
              component="div"
              className="sm:hidden border-b shadow-xl"
              style={{
                ...styles,
                backgroundColor: colorScheme === 'dark' ? 'rgba(26, 27, 30, 0.98)' : 'var(--mantine-color-white)',
                color: 'var(--mantine-color-text)',
                position: 'fixed',
                top: '64px',
                left: 0,
                right: 0,
                zIndex: 41,
                maxHeight: 'calc(100vh - 64px)',
                overflowY: 'auto'
              }}
              radius={0}
            >
              <Container px="md" py="xl">
                {user && (
                  <Stack gap="xs" mb="md" pb="md" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
                    <Group gap="sm">
                      <div style={{ flex: 1 }}>
                        <Text size="md" fw={600} c={colorScheme === 'dark' ? 'white' : 'black'}>{user.userName}</Text>
                        <Text size="sm" c="dimmed">{user.companyName}</Text>
                      </div>
                      <div className="flex flex-col gap-2">
                        {pathname.startsWith("/settings") ? (
                          !hideDashboardButton && (
                            <ActionIcon
                              variant="light"
                              color="brand"
                              onClick={() => {
                                navigate("/");
                                setMobileMenuOpen(false);
                              }}
                              size="lg"
                              radius="md"
                            >
                              <LayoutDashboard size={20} />
                            </ActionIcon>
                          )
                        ) : (
                          !hideSettingsButton && (
                            <ActionIcon
                              variant="light"
                              color="brand"
                              onClick={() => {
                                navigate("/settings/mfa");
                                setMobileMenuOpen(false);
                              }}
                              size="lg"
                              radius="md"
                            >
                              <Settings size={20} />
                            </ActionIcon>
                          )
                        )}
                      </div>
                    </Group>
                  </Stack>
                )}

                {sidebarContent && (
                  <div className="mb-8">
                    {sidebarContent}
                  </div>
                )}

                <div className="pt-6" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
                  <Button
                    variant="light"
                    color="red"
                    fullWidth
                    onClick={handleLogout}
                    leftSection={<LogOut size={18} />}
                    size="md"
                    radius="md"
                  >
                    Logout
                  </Button>
                </div>
              </Container>
            </Paper>
          )}
        </Transition>
      </Portal>

      <main className={`flex-1 ${hideHeader ? "" : "py-6"}`}>
        <Container size={hideHeader ? "100%" : "xl"} px={hideHeader ? 0 : "md"} className="h-full w-full">
          {showSidebar && sidebarContent ? (
            <div className="flex gap-6 items-start relative">
              <aside
                className="hidden sm:block sticky top-24 transition-all duration-300 ease-in-out origin-left"
                style={{
                  width: isSidebarCollapsed ? '0px' : '300px',
                  minWidth: isSidebarCollapsed ? '0px' : '300px',
                  opacity: isSidebarCollapsed ? 0 : 1,
                  overflow: 'hidden',
                  marginRight: isSidebarCollapsed ? '-1.5rem' : '0px',
                  display: isSidebarCollapsed ? 'none' : undefined
                }}
              >
                <div style={{ width: '300px' }}>
                  <Stack gap="md">
                    {user && (
                      <Card
                        withBorder
                        padding="md"
                        radius="md"
                        shadow="sm"
                        style={{
                          backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'var(--mantine-color-white)',
                          borderColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : undefined
                        }}
                      >
                        <Group gap="md">
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text fw={600} size="md" truncate title={user.userName}>
                              {user.userName}
                            </Text>
                            <Group gap={4} wrap="nowrap">
                              <Building2 size={12} style={{ color: 'var(--mantine-color-dimmed)' }} className="shrink-0" />
                              <Text size="xs" c="dimmed" truncate title={user.companyName}>
                                {user.companyName}
                              </Text>
                            </Group>
                            {pathname.startsWith("/settings") ? (
                              !hideDashboardButton && (
                                <Button
                                  variant="subtle"
                                  color={colorScheme === 'dark' ? 'gray' : 'brand'}
                                  size="compact-xs"
                                  mt={4}
                                  leftSection={<LayoutDashboard size={12} />}
                                  onClick={() => navigate("/")}
                                  fullWidth
                                  justify="start"
                                >
                                  Dashboard
                                </Button>
                              )
                            ) : (
                              !hideSettingsButton && (
                                <Button
                                  variant="subtle"
                                  color={colorScheme === 'dark' ? 'gray' : 'brand'}
                                  size="compact-xs"
                                  mt={4}
                                  leftSection={<Settings size={12} />}
                                  onClick={() => navigate("/settings/mfa")}
                                  fullWidth
                                  justify="start"
                                >
                                  Settings
                                </Button>
                              )
                            )}
                          </div>
                        </Group>
                      </Card>
                    )}

                    {sidebarContent}
                  </Stack>
                </div>
              </aside>
              <div className="min-w-0 flex-1">
                {children}
              </div>
            </div>
          ) : (
            <div className="min-w-0">
              {children}
            </div>
          )}
        </Container>
      </main>

      {floatingContent}
    </Box>
  );
}
